import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import {
  Search,
  Filter,
  Download,
  Plus,
  CalendarIcon,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Tickets() {
  const { user, loading: authLoading } = useAuth();
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [startDate, setStartDate] = useState(searchParams.get('start_date') ? new Date(searchParams.get('start_date')) : null);
  const [endDate, setEndDate] = useState(searchParams.get('end_date') ? new Date(searchParams.get('end_date')) : null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  const fetchTickets = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('company_id', companyId);
      if (search) params.append('search', search);
      if (status && status !== 'all') params.append('status', status);
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      params.append('limit', limit.toString());
      params.append('skip', ((page - 1) * limit).toString());

      const response = await fetch(`${API_URL}/api/tickets?${params.toString()}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [companyId, search, status, startDate, endDate, page]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchTickets();
    }
  }, [authLoading, user, companyId, fetchTickets]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/export/csv?company_id=${companyId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tickets_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Export completed');
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('all');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    setSearchParams({});
  };

  const getStatusBadge = (ticketStatus, labStatus) => {
    if (ticketStatus === 'closed' && labStatus === 'rejected') {
      return <span className="status-badge status-rejected">Rejected</span>;
    }
    
    const statusMap = {
      pending_lab: { class: 'status-pending-lab', label: 'Pending Lab' },
      pending_accounts: { class: 'status-pending-accounts', label: 'Pending Accounts' },
      closed: { class: 'status-closed', label: 'Closed' }
    };
    
    const statusInfo = statusMap[ticketStatus] || { class: 'status-pending-lab', label: ticketStatus };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const totalPages = Math.ceil(total / limit);

  const canCreate = user?.role === 'production' || user?.role === 'manager';

  if (authLoading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="tickets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">All Tickets</h1>
            <p className="text-slate-500 mt-1">{total} total tickets</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="h-12 px-4"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            {canCreate && (
              <Button
                onClick={() => navigate('/tickets/new')}
                className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
                data-testid="new-ticket-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Entry
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6" data-testid="filters-section">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by ticket ID, batch number, or product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTickets()}
                className="pl-10 h-12"
                data-testid="search-input"
              />
            </div>

            {/* Toggle Filters */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 px-4"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(status !== 'all' || startDate || endDate) && (
                <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </Button>

            <Button
              onClick={fetchTickets}
              className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
              data-testid="search-btn"
            >
              Search
            </Button>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-12" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending_lab">Pending Lab</SelectItem>
                    <SelectItem value="pending_accounts">Pending Accounts</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-start text-left font-normal"
                      data-testid="start-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-start text-left font-normal"
                      data-testid="end-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-12 w-full"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Tickets Table/Cards */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="tickets-list">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="empty-state">
              <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3>No tickets found</h3>
              <p className="text-slate-500">Try adjusting your filters or create a new entry</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header-row">
                      <th className="table-header-cell">Ticket ID</th>
                      <th className="table-header-cell">Batch Number</th>
                      <th className="table-header-cell">Product</th>
                      <th className="table-header-cell">Quantity</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell">Lab Status</th>
                      <th className="table-header-cell">Accounts</th>
                      <th className="table-header-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr
                        key={ticket.ticket_id}
                        className="table-row cursor-pointer"
                        onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                        data-testid={`ticket-row-${ticket.ticket_id}`}
                      >
                        <td className="table-cell font-mono font-medium text-slate-900">{ticket.ticket_id}</td>
                        <td className="table-cell">{ticket.batch_number}</td>
                        <td className="table-cell">{ticket.product_name}</td>
                        <td className="table-cell">{ticket.quantity} {ticket.unit}</td>
                        <td className="table-cell">{getStatusBadge(ticket.status, ticket.lab_status)}</td>
                        <td className="table-cell">
                          {ticket.lab_status ? (
                            <span className={`status-badge ${ticket.lab_status === 'approved' ? 'status-approved' : 'status-rejected'}`}>
                              {ticket.lab_status}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="table-cell">
                          {ticket.accounts_status ? (
                            <span className="text-xs font-medium text-green-600">
                              Stock Entered
                            </span>
                          ) : '-'}
                        </td>
                        <td className="table-cell">{new Date(ticket.production_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="sm:hidden p-4 space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    className="ticket-card-mobile"
                    onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    data-testid={`ticket-card-${ticket.ticket_id}`}
                  >
                    <div className="ticket-card-header">
                      <span className="ticket-card-id">{ticket.ticket_id}</span>
                      {getStatusBadge(ticket.status, ticket.lab_status)}
                    </div>
                    <div className="ticket-card-body">
                      <span className="ticket-card-label">Batch:</span>
                      <span className="ticket-card-value">{ticket.batch_number}</span>
                      <span className="ticket-card-label">Product:</span>
                      <span className="ticket-card-value">{ticket.product_name}</span>
                      <span className="ticket-card-label">Quantity:</span>
                      <span className="ticket-card-value">{ticket.quantity} {ticket.unit}</span>
                      <span className="ticket-card-label">Date:</span>
                      <span className="ticket-card-value">{new Date(ticket.production_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <span className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
